import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Sun, AlertTriangle, CheckCircle, Home } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PartOOverheatingGuide = () => {
  return (
    <>
      <Helmet>
        <title>Part O Overheating Guide | Building Regulations | Compliance Methods | Energy Performance Direct</title>
        <meta name="description" content="Complete guide to Part O Building Regulations for overheating in new homes. Simplified method, dynamic modelling, risk factors, and mitigation strategies explained." />
        <meta name="keywords" content="Part O guide, overheating building regulations, Part O compliance, overheating risk new build, TM59, simplified method overheating" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/resources/part-o-overheating-guide" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <Link to="/resources" className="text-green-600 hover:underline text-sm">← Back to Resources</Link>
              </div>
              
              <header className="mb-12">
                <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">Building Regulations</span>
                <h1 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Part O Overheating: Complete Compliance Guide</h1>
                <p className="text-xl text-gray-600">Everything you need to know about Part O of the Building Regulations - when it applies, how to comply, and strategies to reduce overheating risk.</p>
              </header>

              <section className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold mt-8 mb-4">What is Part O?</h2>
                <p className="text-gray-700 mb-4">
                  Part O is the section of the Building Regulations (England) that addresses overheating risk in new residential buildings. Introduced on <strong>15 June 2022</strong>, it requires new dwellings to be designed to:
                </p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> Limit unwanted solar gains in summer</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> Provide adequate means to remove excess heat</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> Protect occupants from the health risks of overheating</li>
                </ul>

                <div className="bg-red-50 border border-red-200 rounded-lg p-6 my-6">
                  <div className="flex gap-3">
                    <Sun className="h-6 w-6 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="text-red-800 font-medium">Why Was Part O Introduced?</p>
                      <p className="text-red-700 mt-2">Climate change is increasing summer temperatures. Highly insulated, airtight modern homes retain heat. Overheating is a serious health risk, particularly for vulnerable occupants. Part O ensures new homes are designed to stay cool.</p>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">When Does Part O Apply?</h2>
                <p className="text-gray-700 mb-4">Part O applies to:</p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" /> All new dwellings (houses, flats, bungalows)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" /> Conversions creating new dwellings</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" /> Residential buildings (care homes, student accommodation)</li>
                </ul>
                <p className="text-gray-700 mb-4">It does NOT apply to existing homes (unless converted), extensions to existing homes, or non-residential buildings.</p>

                <h2 className="text-2xl font-bold mt-8 mb-4">Compliance Methods</h2>
                <p className="text-gray-700 mb-4">Part O offers two compliance routes:</p>

                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Card className="border-blue-200">
                    <CardContent className="pt-6">
                      <h3 className="font-bold text-lg mb-3">1. Simplified Method</h3>
                      <p className="text-gray-600 text-sm mb-3">Prescriptive approach using:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Maximum glazing limits</li>
                        <li>• Shading requirements</li>
                        <li>• Minimum ventilation openings</li>
                        <li>• Cross-ventilation rules</li>
                      </ul>
                      <p className="text-sm text-blue-600 mt-3 font-medium">Best for: Standard designs</p>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200">
                    <CardContent className="pt-6">
                      <h3 className="font-bold text-lg mb-3">2. Dynamic Modelling</h3>
                      <p className="text-gray-600 text-sm mb-3">Detailed simulation showing:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Hour-by-hour temperatures</li>
                        <li>• Overheating hours below threshold</li>
                        <li>• CIBSE TM59 criteria</li>
                        <li>• Specific room performance</li>
                      </ul>
                      <p className="text-sm text-green-600 mt-3 font-medium">Best for: Complex designs</p>
                    </CardContent>
                  </Card>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Overheating Risk Factors</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-3 text-red-800">Higher Risk</h3>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" /> London / South East location</li>
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" /> South/West facing bedrooms</li>
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" /> Single aspect flats</li>
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" /> Large glazing areas</li>
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" /> Top floor units</li>
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" /> No external shading</li>
                      </ul>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-3 text-green-800">Mitigation Strategies</h3>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /> External shading (overhangs)</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /> Reduced glazing ratios</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /> Low g-value glazing</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /> Cross-ventilation</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /> Purge ventilation</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /> Thermal mass</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Part O vs TM59</h2>
                <p className="text-gray-700 mb-4">
                  Part O and CIBSE TM59 both address overheating, but they're different:
                </p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li><strong>Part O</strong> is a Building Regulations requirement (mandatory for all new dwellings in England)</li>
                  <li><strong>TM59</strong> is a CIBSE methodology often required by planning authorities, especially in London</li>
                </ul>
                <p className="text-gray-700 mb-4">Some projects need both assessments. We can advise which applies to your project.</p>

                <h2 className="text-2xl font-bold mt-8 mb-4">Related Services</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Link to="/part-o-overheating" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Part O Assessments</h3>
                        <p className="text-gray-600 text-sm">Professional overheating assessments</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/sap-calculations" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">SAP Calculations</h3>
                        <p className="text-gray-600 text-sm">Part L compliance for new builds</p>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </section>

              <div className="mt-12 p-6 bg-green-50 rounded-lg text-center">
                <h3 className="text-xl font-bold mb-3">Need a Part O Assessment?</h3>
                <p className="text-gray-600 mb-4">Simplified method or dynamic modelling - we can help.</p>
                <Link to="/booking">
                  <Button className="bg-green-600 hover:bg-green-700">
                    Request Quote <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default PartOOverheatingGuide;
